/**
 * Hashtag timeline service for fetching posts by hashtag
 * from AT Protocol (Bluesky), Mastodon, and Pixelfed.
 */
import { Agent, type AppBskyFeedDefs } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import type { MastodonSession } from '@/lib/mastodon'
import { ensureValidSession, parseLinkHeader } from '@/lib/mastodon'
import {
    type PixelfedSession,
    ensureValidSession as ensureValidPixelfedSession,
} from '@/lib/pixelfed'
import { isSafeHostname } from '@/lib/apUtils'
import type { MastodonStatus } from '@/types/mastodon'
import type { PostsPage } from '@/types/posts'
import { mapFeedViewPost } from '@/services/postsService'
import { mapMastodonStatus } from '@/services/mastodonService'
import { mapPixelfedStatus } from '@/services/pixelfedService'
import { POST_SEARCH_PAGE_SIZE } from '@/constants/search'

const FETCH_TIMEOUT_MS = 10_000

/**
 * Fetch Bluesky posts for a hashtag.
 * Uses searchPosts with sort=latest so newest posts appear first,
 * matching Mastodon's chronological hashtag timeline.
 * Always uses the session agent (PDS-proxied) so requests are authenticated.
 * This also pins hashtag search to the default Bluesky AppView regardless of
 * the selected AppView: Blacksky's app.bsky.feed.searchPosts backend returns a
 * persistent 502 (mackinac#2985). Revisit if Blacksky's search recovers.
 */
export async function fetchBlueskyHashtagPosts(
    tag: string,
    session: OAuthSession,
    cursor?: string
): Promise<PostsPage> {
    const agent = new Agent(session)
    const { data } = await agent.app.bsky.feed.searchPosts({
        q: `#${tag}`,
        sort: 'latest',
        limit: POST_SEARCH_PAGE_SIZE,
        cursor,
    })

    if (!data?.posts) {
        throw new Error('Invalid response from Bluesky post search')
    }

    const posts = data.posts.map((postView: AppBskyFeedDefs.PostView) =>
        mapFeedViewPost({ post: postView })
    )

    // Batch-fetch parent posts for replies so they render inline
    // instead of each ParentPostPreview making individual API calls.
    const parentUris = posts
        .filter(p => p.replyParentUri && !p.parentPost)
        .map(p => p.replyParentUri!)
    const uniqueParentUris = [...new Set(parentUris)]

    if (uniqueParentUris.length > 0) {
        try {
            // getPosts accepts max 25 URIs per call
            const batches: string[][] = []
            for (let i = 0; i < uniqueParentUris.length; i += 25) {
                batches.push(uniqueParentUris.slice(i, i + 25))
            }
            const responses = await Promise.all(
                batches.map(batch => agent.app.bsky.feed.getPosts({ uris: batch }))
            )
            const parentMap = new Map<string, AppBskyFeedDefs.PostView>()
            for (const response of responses) {
                for (const postView of response.data.posts) {
                    parentMap.set(postView.uri, postView)
                }
            }

            // Attach parent post data inline
            for (const post of posts) {
                if (post.replyParentUri && !post.parentPost && parentMap.has(post.replyParentUri)) {
                    post.parentPost = mapFeedViewPost({
                        post: parentMap.get(post.replyParentUri)!,
                    })
                }
            }
        } catch (err) {
            // Non-critical: parent posts will still load individually via useParentPost
            console.warn('[hashtag] batch parent fetch failed:', err)
        }
    }

    return { posts, cursor: data.cursor }
}

/**
 * Fetch Mastodon posts for a hashtag using the dedicated timeline endpoint.
 * Uses GET /api/v1/timelines/tag/:hashtag with max_id pagination via Link headers.
 * Same SSRF protection and pagination pattern as fetchMastodonTimeline.
 */
export async function fetchMastodonHashtagPosts(
    tag: string,
    mastodonSession: MastodonSession,
    cursor?: string
): Promise<PostsPage> {
    const session = await ensureValidSession(mastodonSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(POST_SEARCH_PAGE_SIZE))
    if (cursor) {
        params.set('max_id', cursor)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${session.instance_url}/api/v1/timelines/tag/${encodeURIComponent(tag)}?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Mastodon API error: ${res.status}`)
        }

        const statuses: MastodonStatus[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        const posts = statuses.map(s => mapMastodonStatus(s, session.instance_url))

        // Extract cursor from the next page URL's max_id param.
        // Only trust URLs from the same origin to prevent token leakage via crafted Link headers.
        // Falls back to last status ID when Link header is missing (robust for all instances).
        let nextCursor: string | undefined
        if (nextUrl) {
            try {
                const nextParsed = new URL(nextUrl)
                if (nextParsed.origin === parsed.origin) {
                    nextCursor = nextParsed.searchParams.get('max_id') ?? undefined
                } else {
                    nextCursor = statuses.length > 0 ? statuses[statuses.length - 1].id : undefined
                }
            } catch {
                if (statuses.length > 0) {
                    nextCursor = statuses[statuses.length - 1].id
                }
            }
        } else if (statuses.length > 0) {
            // No Link header — use last status ID as cursor for max_id pagination
            nextCursor = statuses[statuses.length - 1].id
        }

        return { posts, mastodonCursor: nextCursor }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch Pixelfed posts for a hashtag using the dedicated timeline endpoint.
 * Pixelfed implements the Mastodon-compatible hashtag timeline API.
 * Uses GET /api/v1/timelines/tag/:hashtag with max_id pagination via Link headers.
 */
export async function fetchPixelfedHashtagPosts(
    tag: string,
    pixelfedSession: PixelfedSession,
    cursor?: string
): Promise<PostsPage> {
    const session = await ensureValidPixelfedSession(pixelfedSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(POST_SEARCH_PAGE_SIZE))
    if (cursor) {
        params.set('max_id', cursor)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${session.instance_url}/api/v1/timelines/tag/${encodeURIComponent(tag)}?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Pixelfed API error: ${res.status}`)
        }

        const statuses: MastodonStatus[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        const posts = statuses.map(s => mapPixelfedStatus(s, session.instance_url))

        let nextCursor: string | undefined
        if (nextUrl) {
            try {
                const nextParsed = new URL(nextUrl)
                if (nextParsed.origin === parsed.origin) {
                    nextCursor = nextParsed.searchParams.get('max_id') ?? undefined
                } else {
                    nextCursor = statuses.length > 0 ? statuses[statuses.length - 1].id : undefined
                }
            } catch {
                if (statuses.length > 0) {
                    nextCursor = statuses[statuses.length - 1].id
                }
            }
        } else if (statuses.length > 0) {
            nextCursor = statuses[statuses.length - 1].id
        }

        return { posts, pixelfedCursor: nextCursor }
    } finally {
        clearTimeout(timeout)
    }
}
